import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Player, Mode } from '../components/room/types';
import { Stack } from '@mui/system';
import { paperDialogStyle } from './styles';
import InfoIcon from '@mui/icons-material/Info';
import { resizeImage } from '../api/cloudinary';
import Countdown, { zeroPad } from 'react-countdown';
import { Profile } from '@par/shared/types/player';
import { getAvatarSrc } from '../components/room/utils';

let timeout: any;

type PropTypes = {
  bannedCategories?: string[];
  confirmedReady: boolean;
  handleReadyCancelClick: (username: string) => void;
  handleReadyClick: () => void;
  handleReadyExpired: () => void;
  mode?: Mode;
  openConfirmReadyModal: {
    open: boolean;
    username?: string;
    delayedClose?: boolean;
    confirmReadyExpiresAt?: number;
    autoStartIn?: number;
  };
  players?: Record<string, Player>;
  profile: Profile;
  ranked?: boolean;
  tournament?: number;
};

export const ConfirmReadyModal = ({
  bannedCategories,
  confirmedReady,
  handleReadyCancelClick,
  handleReadyClick,
  handleReadyExpired,
  mode,
  openConfirmReadyModal,
  players,
  profile,
  ranked,
  tournament,
}: PropTypes) => {
  const [open, setOpen] = useState(false);
  const [userNotReady, setUserNotReady] = useState<string>();
  const [anotherPlayerNotReady, setAnotherPlayerNotReady] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const { open, username, delayedClose } = openConfirmReadyModal;
    if (!open && !delayedClose) {
      setOpen(false);
      return;
    }
    setUserNotReady(username);
    if (!open && username !== profile.username && delayedClose) {
      setAnotherPlayerNotReady(true);
      timeout = setTimeout(() => {
        setAnotherPlayerNotReady(false);
        setOpen(open);
      }, 3000);
      return;
    }
    clearTimeout(timeout);
    setAnotherPlayerNotReady(false);
    setOpen(open);
    setExpired(false);
  }, [openConfirmReadyModal]);

  const theme = useTheme();

  // const getAverageMmr = () => {
  //   if (!players) return;
  //   let sum = 0;
  //   for (const key of Object.keys(players)) {
  //     sum = sum + (players[key].rankedRating || 0);
  //   }
  //   return Math.round(sum / Object.keys(players).length);
  // };

  const countdownRenderer = ({ minutes, seconds }: any) => {
    return (
      <span>
        {zeroPad(minutes)}:{zeroPad(seconds)}
      </span>
    );
  };

  const handleConfirmReadyExpired = () => {
    if (openConfirmReadyModal.autoStartIn) return;
    setExpired(true);
    handleReadyExpired();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        }
      }}
      fullWidth
      PaperProps={{
        style: paperDialogStyle(theme),
      }}
    >
      <DialogTitle>
        {tournament && openConfirmReadyModal.autoStartIn !== undefined ? (
          <Typography variant="h5" textAlign={'center'}>
            TOURNAMENT PLAYERS
          </Typography>
        ) : (
          <Typography variant="h5" textAlign={'center'}>
            WAITING FOR PLAYERS
          </Typography>
        )}
        {openConfirmReadyModal.confirmReadyExpiresAt &&
        !openConfirmReadyModal.autoStartIn ? (
          <Typography variant="h6" textAlign={'center'}>
            <Countdown
              date={openConfirmReadyModal.confirmReadyExpiresAt}
              renderer={countdownRenderer}
              onComplete={handleConfirmReadyExpired}
            />
          </Typography>
        ) : null}
        {ranked ? (
          <Stack
            direction="row"
            spacing={2}
            sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}
          >
            <Tooltip
              title={
                'A category will be banned if banned by at least 50% of the players in the current game'
              }
              placement="top"
            >
              <InfoIcon />
            </Tooltip>
            <Typography variant="body1">Banned Categories:</Typography>
            {bannedCategories && bannedCategories?.length > 0 && !tournament ? (
              bannedCategories?.map((c, i) => (
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.warning.main }}
                >
                  {c}
                  {bannedCategories.length > 1 && i === 0 ? ',' : ''}
                </Typography>
              ))
            ) : (
              <Typography variant="body1">none</Typography>
            )}
          </Stack>
        ) : null}
        {/* {mode === 'sit-n-go' && ranked ? (
          <Typography variant="body1" textAlign={'center'}>
            Average MMR: {getAverageMmr()}
          </Typography>
        ) : null} */}
      </DialogTitle>
      <DialogContent>
        {players &&
          Object.values(players).map((p) => (
            <Stack direction={'row'} width="100%">
              <Stack width="70%" spacing={2}>
                <Stack direction={'row'} width="100%" spacing={2}>
                  <Avatar
                    src={getAvatarSrc(p.avatarPublicId, p.avatarUrl, 50, 50)}
                    variant="rounded"
                    sx={{
                      height: 40,
                      width: 40,
                    }}
                  >
                    {!p.avatarPublicId ? p.username.charAt(0) : null}
                  </Avatar>
                  <Typography
                    sx={{
                      lineHeight: '40px',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {p.username}
                  </Typography>
                  {ranked ? (
                    <Typography
                      fontWeight={600}
                      sx={{
                        lineHeight: '40px',
                        color: theme.palette.ranked.main,
                      }}
                    >
                      (
                      {(p.rankedMatchGamesPlayed || 0) +
                        (p.rankedSitAndGoGamesPlayed || 0) <
                      10
                        ? 'calibrating'
                        : p.rankedRating}
                      )
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
              <Stack width="30%" spacing={2} textAlign={'right'}>
                {userNotReady === p.username ? (
                  <Typography
                    sx={{
                      color: 'error.main',
                      lineHeight: '40px',
                    }}
                  >
                    Not Ready
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      color:
                        openConfirmReadyModal.autoStartIn || p.confirmedReady
                          ? 'success.main'
                          : 'warning.main',
                      lineHeight: '40px',
                    }}
                  >
                    {openConfirmReadyModal.autoStartIn
                      ? 'Joined'
                      : p.confirmedReady
                      ? 'Ready'
                      : 'Waiting...'}
                  </Typography>
                )}
              </Stack>
            </Stack>
          ))}
      </DialogContent>
      <DialogActions>
        {openConfirmReadyModal.autoStartIn ? (
          <Box
            sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <Typography variant="h6" textAlign={'center'}>
              Starting in:{' '}
              <Countdown
                date={openConfirmReadyModal.autoStartIn}
                renderer={countdownRenderer}
                onComplete={handleConfirmReadyExpired}
              />
            </Typography>
          </Box>
        ) : null}
        {!openConfirmReadyModal.autoStartIn ? (
          <>
            <Button
              variant="text"
              onClick={() => handleReadyCancelClick(profile.username)}
              fullWidth
              disabled={confirmedReady || anotherPlayerNotReady || expired}
            >
              NOT READY
            </Button>
            <Button
              variant="contained"
              onClick={handleReadyClick}
              fullWidth
              disabled={confirmedReady || anotherPlayerNotReady || expired}
            >
              READY
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};
